import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Truck, ShieldCheck } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import api from "@/lib/api";
import ProductCard from "@/components/ProductCard";

const Home = () => {
  const { lang, t } = useLang();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/products?featured=true&limit=8").then((r) => setProducts(r.data.items || []));
    api.get("/categories").then((r) => setCategories(r.data || []));
  }, []);

  const heroTitle = t("home.heroTitle");

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative grain-bg min-h-[88vh] flex items-center overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <img
            src="https://images.pexels.com/photos/13898221/pexels-photo-13898221.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1600"
            alt="Amazypet hero"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#FAF9F6] via-[#FAF9F6]/85 to-transparent" />
        </div>

        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 w-full grid md:grid-cols-2 gap-10 py-20">
          <div className="space-y-8 fade-up">
            <p className="overline">{t("home.heroOverline")}</p>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[1.05] tracking-tight">
              {heroTitle.split("\n").map((line, i) => (
                <span key={i} className="block">
                  {line}
                </span>
              ))}
            </h1>
            <p className="text-base lg:text-lg text-[#736B66] max-w-md leading-relaxed">
              {t("home.heroSubtitle")}
            </p>
            <div className="flex flex-wrap gap-4 pt-2">
              <Link to="/shop" className="luxury-btn" data-testid="hero-shop-now">
                {t("home.shopNow")} <ArrowRight size={16} className="hero-arrow" />
              </Link>
              <Link to="/shop?featured=true" className="luxury-btn-outline" data-testid="hero-explore">
                {t("home.exploreCollection")}
              </Link>
            </div>
          </div>
          <div className="hidden md:block" />
        </div>
      </section>

      {/* CATEGORIES BENTO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24" data-testid="categories-section">
        <div className="mb-12 max-w-2xl">
          <p className="overline mb-3">{t("home.categoriesOverline")}</p>
          <h2 className="font-serif text-4xl lg:text-5xl">{t("home.categoriesTitle")}</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((cat, idx) => (
            <Link
              key={cat.id}
              to={`/shop?category=${cat.slug}`}
              data-testid={`category-${cat.slug}`}
              className={`group relative overflow-hidden lift fade-up ${idx === 0 ? "lg:row-span-2 lg:aspect-auto aspect-[4/5]" : "aspect-[4/5]"}`}
              style={{ animationDelay: `${idx * 0.06}s` }}
            >
              <img
                src={cat.image}
                alt={cat.name_el}
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                style={{
                  objectPosition:
                    cat.slug === "skylos"
                      ? "30% center"
                      : cat.slug === "erpeta"
                        ? "20% center"
                        : "center",
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#2C1E16]/80 via-[#2C1E16]/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                <p className="overline text-[#D4AF37] mb-2">{cat.slug}</p>
                <h3 className="font-serif text-3xl lg:text-4xl">
                  {lang === "el" ? cat.name_el : cat.name_en}
                </h3>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24" data-testid="featured-products">
        <div className="flex items-end justify-between mb-12 flex-wrap gap-4">
          <div>
            <p className="overline mb-3">{t("home.featuredOverline")}</p>
            <h2 className="font-serif text-4xl lg:text-5xl">{t("home.featuredTitle")}</h2>
          </div>
          <Link to="/shop" className="link-underline text-sm tracking-widest uppercase" data-testid="see-all-products">
            See all →
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10">
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* VALUES */}
      <section className="bg-[#F5F2ED] py-24" data-testid="values-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <h2 className="font-serif text-4xl lg:text-5xl text-center mb-16">{t("home.valuesTitle")}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { icon: <Sparkles size={28} />, title: t("home.v1Title"), desc: t("home.v1Desc") },
              { icon: <Truck size={28} />, title: t("home.v2Title"), desc: t("home.v2Desc") },
              { icon: <ShieldCheck size={28} />, title: t("home.v3Title"), desc: t("home.v3Desc") },
            ].map((v, i) => (
              <div key={i} className="text-center space-y-4 fade-up" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-white border border-[#EBE5DF] text-[#D4AF37]">
                  {v.icon}
                </div>
                <h3 className="font-serif text-2xl">{v.title}</h3>
                <p className="text-sm text-[#736B66] leading-relaxed max-w-xs mx-auto">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 py-24 text-center" data-testid="newsletter">
        <p className="overline mb-3">Amazypet Society</p>
        <h2 className="font-serif text-4xl lg:text-5xl mb-4">{t("home.newsletterTitle")}</h2>
        <p className="text-[#736B66] mb-8 max-w-lg mx-auto">{t("home.newsletterDesc")}</p>
        <form
          onSubmit={(e) => { e.preventDefault(); alert("Thanks!"); }}
          className="flex max-w-md mx-auto gap-0 border-b border-[#2C1E16]"
        >
          <input
            type="email"
            required
            placeholder="email@example.com"
            className="luxury-input flex-1 border-b-0"
            data-testid="newsletter-email"
          />
          <button className="px-6 py-2 text-xs tracking-widest uppercase font-semibold hover:text-[#D4AF37]" data-testid="newsletter-submit">
            {t("home.newsletterCta")}
          </button>
        </form>
      </section>
    </div>
  );
};

export default Home;
